import React, { useContext, useCallback } from 'react';
import classnames from 'classnames';
import { PapiDeviationPoll } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import PollViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/Poll';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import TextRenderer from '../TextRenderer';
import PollOptionThumb from './PollOptionThumb';
import { BiContextProvidedValue, BiData, PollVoteBiEvent } from '@wix/da-bi/pkg/events';

import localStyles from './PollOption.scss';

const DEFAULT_IMAGE_SIZE_MOBILE = 72;
const DEFAULT_IMAGE_SIZE_DESKTOP = 84;

export interface Props {
  answer: PapiDeviationPoll['answers'][0];
  canVote: boolean;
  className?: string;
  feedView?: boolean;
  draftJsThemeOverride?: any;
  answerClassName?: string;
  stylesheet?: Record<string, string>;
  imageSize?: number;
  hoverImageSize?: number;
  onClick: (answerId: number) => void;
}

const PollOption: React.FC<Props> = ({
  answer,
  className,
  canVote,
  draftJsThemeOverride,
  answerClassName,
  stylesheet: s = localStyles,
  imageSize,
  hoverImageSize = 0,
  onClick,
}) => {
  const isMobile = useContext(MobileContext);
  const logEvent = useBiLogger();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!answer.answerId || !canVote) {
        return;
      }

      // should allow clicks through that are on links inside the answer
      const parentLink = (event.target as HTMLElement).closest(
        `.${s['answer-text']} a`
      ) as HTMLAnchorElement;
      if (parentLink) {
        // force said link into a new tab
        window.open(parentLink.href, '', 'noopener');
        event.preventDefault();
        return;
      }

      logEvent(BiData<PollVoteBiEvent>({ evid: 115, pollid: BiContextProvidedValue }));
      onClick(answer.answerId);
    },
    [answer.answerId, canVote, logEvent, onClick, s]
  );

  const thumbSize =
    imageSize || (isMobile ? DEFAULT_IMAGE_SIZE_MOBILE : DEFAULT_IMAGE_SIZE_DESKTOP);
  const imageBlockStyles = {
    width: thumbSize,
    height: thumbSize,
    flexBasis: thumbSize,
  };
  const adjX = thumbSize + 4;
  const adjY = -(hoverImageSize / 2 - thumbSize / 2);
  const hoverStyles = hoverImageSize
    ? {
        width: hoverImageSize,
        height: hoverImageSize,
        transform: `translate(${adjX}px, ${adjY}px)`,
      }
    : {};

  const hasImage = !!answer.deviationId;
  return (
    <div className={classnames(s['root'], canVote && s['can-vote'], className)}>
      {hasImage && (
        <div className={s['image-block']} style={imageBlockStyles}>
          <PollOptionThumb deviation={answer.image} size={thumbSize} />
        </div>
      )}
      {!!hoverImageSize && hasImage && (
        <div className={s['image-block-hover']} style={hoverStyles}>
          <PollOptionThumb deviation={answer.image} size={hoverImageSize} />
        </div>
      )}

      <div
        className={classnames(s['text-block'], hasImage && s['has-image'])}
        onClick={handleClick}
      >
        {answer.textContent && (
          <TextRenderer
            htmlExcerptLegacy
            isPollWithHtmlExcerpt
            className={classnames(s['answer-text'], answerClassName)}
            textContent={answer.textContent}
            tipTapViewer={PollViewer}
            cookieType={MeasuredCookieType.DEVIATION_DESCRIPTION}
            defaultSize={{
              desktop: { width: 780, height: 800 },
              mobile: { width: 380, height: 400 },
            }}
            viewerKey={answer.answerId}
            legacyTheme="poll-answer"
            {...(draftJsThemeOverride ? { draftJsThemeOverride } : {})}
          />
        )}
        {!canVote && (
          <>
            <div className={s['progress-bar']}>
              <div
                className={s['progress-bar__value']}
                style={{ width: `${answer.percentvotes}%` }}
              />
            </div>
            <div className={s['percent-votes']}>{answer.percentvotes}%</div>
          </>
        )}
      </div>
    </div>
  );
};
PollOption.displayName = 'PollOption';

export default PollOption;
